import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiError } from "@/lib/api/client";
import { eventsApi } from "@/lib/api/endpoints";
import { useAsync } from "@/lib/hooks/useAsync";
import { useDebouncedEffect } from "@/lib/hooks/useDebouncedEffect";
import { Button, Modal, Spinner, Tabs, useToast } from "@/components/ui";
import type { TabItem } from "@/components/ui";
import type { DraftConfig } from "@/lib/types/config";
import type { EventDetail } from "@/lib/types/api";
import { useAuth } from "@/features/auth/AuthContext";
import { can, canManageAccess } from "@/lib/utils/permissions";
import { BuilderProvider, useSaveIndicator, type SaveState } from "./BuilderContext";
import { LivePreview } from "./components/LivePreview";
import { BrandWordmark } from "./components/BrandMark";
import {
  IconBack, IconBadgeCheck, IconCheck, IconClipboard, IconDownload, IconExternal,
  IconEye, IconFrame, IconGallery, IconLink, IconMonitor, IconPalette, IconSparkles,
} from "./components/icons";

import { GeneralTab } from "./tabs/GeneralTab";
import { ExperienceTab } from "./tabs/ExperienceTab";
import { FlowTab } from "./tabs/FlowTab";
import { LookTab } from "./tabs/LookTab";
import { ContentTab } from "./tabs/ContentTab";
import { PricingTab } from "./tabs/PricingTab";
import { MomentsTab } from "./tabs/MomentsTab";
import { FrameQrTab } from "./tabs/FrameQrTab";
import { HardwareTab } from "./tabs/HardwareTab";
import { AccessTab } from "./tabs/AccessTab";

/**
 * Ten tabs, ordered the way an operator actually works: what is this event, what
 * format, what journey, how does it look, what's in it, what does it cost.
 *
 * Four tabs were removed getting here, and it's worth knowing why so they don't
 * creep back:
 *
 *   Snapshot  → Flow.    Three settings, one of which ("enabled") was also a
 *                        toggle in General, so the two screens could disagree.
 *   Assets    → Look.    Four audio uploads and two copy fields. Nobody looks
 *                        for the welcome sound anywhere but the welcome tab.
 *   Acts      → Content. Same question as Vibes — "what can guests choose?" —
 *                        and it stayed visible even when the participant screen
 *                        was switched off.
 *   Preview   → gone.    It duplicated the live preview already docked on the
 *                        right of every tab. A tab whose job is to show you
 *                        something permanently on screen is a tab that teaches
 *                        people the tab bar isn't worth reading.
 *
 * And one was added: Flow, because "how many screens does a guest walk through"
 * is the highest-leverage decision in the console and it had no home.
 */
const TABS: TabItem[] = [
  { id: "general", label: "General", icon: <IconClipboard size={14} /> },
  { id: "experience", label: "Experience", icon: <IconBadgeCheck size={14} /> },
  { id: "flow", label: "Flow", icon: <IconEye size={14} /> },
  { id: "look", label: "Look", icon: <IconPalette size={14} /> },
  { id: "content", label: "Content", icon: <IconGallery size={14} /> },
  { id: "output", label: "Output", icon: <IconFrame size={14} /> },
  { id: "pricing", label: "Pricing", icon: <IconDownload size={14} /> },
  { id: "moments", label: "Moments", icon: <IconSparkles size={14} /> },
  { id: "hardware", label: "Hardware", icon: <IconMonitor size={14} /> },
  { id: "access", label: "Access", icon: <IconBadgeCheck size={14} /> },
];

/**
 * Tabs the current user has any business opening.
 *
 * Pricing is hidden without the pricing permission because the server strips
 * price changes from the payload — leaving the tab visible would let someone
 * type a price, see it save, and never learn it was discarded.
 *
 * Access is hidden from event admins because they cannot grant access at all.
 *
 * Both rules are enforced server-side too. This is about not showing people
 * controls that will lie to them.
 */
function visibleTabs(opts: { pricing: boolean; access: boolean }): TabItem[] {
  return TABS.filter((tab) => {
    if (tab.id === "pricing") return opts.pricing;
    if (tab.id === "access") return opts.access;
    return true;
  });
}

const AUTOSAVE_DELAY_MS = 900;

export function SiteBuilderPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const numericId = Number(eventId);
  const request = useAsync(() => eventsApi.get(numericId), [numericId]);

  if (request.loading) {
    return <div className="grid min-h-screen place-items-center"><Spinner size={28} className="text-accent" /></div>;
  }
  if (request.error || !request.data) {
    return (
      <div className="grid min-h-screen place-items-center px-6 text-center">
        <div className="flex flex-col items-center gap-3">
          <p className="text-sm text-danger">{request.error?.message ?? "Event not found"}</p>
          <a href="/admin" className="text-sm text-accent hover:underline">Back to dashboard</a>
        </div>
      </div>
    );
  }

  return <Builder initialEvent={request.data} />;
}

function Builder({ initialEvent }: { initialEvent: EventDetail }) {
  const navigate = useNavigate();
  const toast = useToast();

  const { user } = useAuth();
  const mayPrice = can(user, initialEvent.id, "pricing");
  const mayPublish = can(user, initialEvent.id, "publish");
  const mayManageAccess = canManageAccess(user);
  const tabs = visibleTabs({ pricing: mayPrice, access: mayManageAccess });

  const [event, setEvent] = useState<EventDetail>(initialEvent);
  const [tab, setTab] = useState("general");
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [previewVersion, setPreviewVersion] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [validation, setValidation] = useState<{ open: boolean; errors?: unknown }>({ open: false });
  // Set when a save was refused — the edit is parked, not live.
  const [liveErrors, setLiveErrors] = useState<unknown>(null);
  const [published, setPublished] = useState<{ open: boolean; url?: string }>({ open: false });

  // Seed the draft from whatever is unpublished, falling back to the published
  // config so an operator editing a live event starts from what guests see.
  const [draft, setDraft] = useState<DraftConfig>(
    () => (initialEvent.draft_config ?? initialEvent.config ?? {}) as DraftConfig,
  );

  const dirty = useRef(false);
  const indicator = useSaveIndicator(saveState);

  const onDraftChange = useCallback((next: DraftConfig) => {
    dirty.current = true;
    setSaveState("dirty");
    setDraft(next);
  }, []);

  // Autosave. Without the debounce this would PATCH once per keystroke.
  useDebouncedEffect(() => {
    if (!dirty.current) return;

    let cancelled = false;
    setSaveState("saving");

    eventsApi.saveDraft(event.id, draft)
      .then((result) => {
        if (cancelled) return;
        dirty.current = false;

        if (result.applied) {
          setSaveState("live");
          setLiveErrors(null);
        } else {
          // The live booth kept the last good config. Show why, right here,
          // rather than making the operator discover it at publish time.
          setSaveState("rejected");
          setLiveErrors(result.errors ?? null);
        }

        setPreviewVersion((version) => version + 1);
      })
      .catch((error: Error) => {
        if (cancelled) return;
        setSaveState("error");
        toast.error(error.message || "Could not save");
      });

    return () => { cancelled = true; };
  }, AUTOSAVE_DELAY_MS, [draft]);

  const boothUrl = `${window.location.origin}/${event.slug}`;

  // Losing an in-flight edit to a stray tab close is worse than a warning.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty.current) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  /**
   * Turns the booth link on.
   *
   * Configuration is already live — every save applies immediately — so this is
   * about availability, not content. It refuses if the current config wouldn't
   * run, because going live broken is worse than not going live at all.
   */
  async function publish() {
    setPublishing(true);
    try {
      if (dirty.current) {
        await eventsApi.saveDraft(event.id, draft);
        dirty.current = false;
      }

      const result = await eventsApi.publish(event.id).catch((error) => {
        if (error instanceof ApiError && error.details !== undefined) {
          setValidation({ open: true, errors: error.details });
          return null;
        }
        throw error;
      });

      if (!result) return;

      setEvent((current) => ({
        ...current,
        status: (result.status as EventDetail["status"]) ?? "published",
        draft_config_present: false,
      }));
      setSaveState("live");
      setLiveErrors(null);
      setPreviewVersion((version) => version + 1);
      setPublished({ open: true, url: result.booth_url ?? boothUrl });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not turn the link on");
    } finally {
      setPublishing(false);
    }
  }

  return (
    <BuilderProvider
      event={event}
      draft={draft}
      saveState={saveState}
      previewVersion={previewVersion}
      onDraftChange={onDraftChange}
      onEventChange={setEvent}
      onPreviewStale={() => setPreviewVersion((version) => version + 1)}
    >
      <div className="flex h-screen flex-col overflow-hidden lg:flex-row">
        <div className="flex min-w-0 flex-1 flex-col border-r border-ink-600">
          <header className="flex h-16 shrink-0 items-center gap-4 border-b border-ink-600 px-5">
            <Button variant="secondary" size="sm" icon={<IconBack size={14} />} onClick={() => navigate("/admin")}>
              Back
            </Button>

            <span className="hidden shrink-0 items-center gap-2 sm:flex">
              <BrandWordmark className="h-5" />
            </span>

            <div className="flex min-w-0 flex-col leading-tight">
              <span className="truncate text-sm font-semibold">Site Builder</span>
              <span className="truncate font-mono text-2xs text-haze-400">
                /{event.slug}
                {saveState === "dirty" || event.draft_config_present ? " · draft config present" : ""}
              </span>
            </div>

            <Button
              variant="outline"
              size="sm"
              icon={<IconExternal size={13} />}
              onClick={() => window.open(boothUrl, "_blank", "noopener")}
            >
              View Booth
            </Button>

            <div className="ml-auto flex items-center gap-4">
              <div className="hidden flex-col items-end leading-tight sm:flex">
                <span className={`text-2xs ${indicator.tone}`}>{indicator.label}</span>
                <span className="text-2xs text-haze-400">
                  {event.status === "published" ? "link is on" : "link is off"}
                </span>
              </div>
              {/* Showing the link to anyone who can reach the event is fine —
                  it's the address guests use. Turning it on is a permission. */}
              {(event.status === "published" || mayPublish) && (
                <Button
                  variant={event.status === "published" ? "secondary" : "primary"}
                  size="md"
                  loading={publishing}
                  icon={event.status === "published" ? <IconLink size={15} /> : <IconCheck size={15} />}
                  onClick={publish}
                >
                  {event.status === "published" ? "Booth link" : "Turn link on"}
                </Button>
              )}
            </div>
          </header>

          {liveErrors != null && (
            <div className="shrink-0 border-b border-danger/40 bg-danger/10 px-5 py-2.5">
              <p className="text-xs font-semibold text-danger">
                This change isn't live — the booth is still running the last working version.
              </p>
              <ul className="mt-1 flex flex-col gap-0.5">
                {flattenErrors(liveErrors).slice(0, 4).map((line) => (
                  <li key={line} className="text-2xs leading-relaxed text-haze-200">{line}</li>
                ))}
              </ul>
            </div>
          )}

          <Tabs tabs={tabs} active={tab} onChange={setTab} className="shrink-0 px-3" />

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
            <div className="mx-auto max-w-3xl animate-fade-in">
              {tab === "general" && <GeneralTab />}
              {tab === "experience" && <ExperienceTab />}
              {tab === "flow" && <FlowTab />}
              {tab === "look" && <LookTab />}
              {tab === "content" && <ContentTab />}
              {tab === "output" && <FrameQrTab />}
              {tab === "pricing" && mayPrice && <PricingTab />}
              {tab === "moments" && <MomentsTab />}
              {tab === "hardware" && <HardwareTab />}
              {tab === "access" && mayManageAccess && <AccessTab eventId={event.id} />}
            </div>
          </div>
        </div>

        <aside className="hidden w-[45vw] max-w-[900px] shrink-0 lg:block">
          <LivePreview slug={event.slug} version={previewVersion} />
        </aside>
      </div>

      <ValidationModal
        open={validation.open}
        errors={validation.errors}
        onClose={() => setValidation({ open: false })}
      />

      <Modal
        open={published.open}
        onClose={() => setPublished({ open: false })}
        title="The booth is live"
        description="Open this on a kiosk. Anything you change from here is live immediately — no need to publish again."
        footer={<Button variant="primary" onClick={() => setPublished({ open: false })}>Done</Button>}
      >
        <div className="flex flex-col gap-3">
          <code className="rounded-lg bg-ink-800 px-3 py-2.5 font-mono text-sm text-accent">
            {published.url ?? boothUrl}
          </code>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => {
                void navigator.clipboard.writeText(published.url ?? boothUrl).then(
                  () => toast.success("Link copied"),
                  () => toast.error("Clipboard access was blocked"),
                );
              }}
            >
              Copy link
            </Button>
            <Button
              size="sm"
              variant="outline"
              icon={<IconExternal size={13} />}
              onClick={() => window.open(published.url ?? boothUrl, "_blank", "noopener")}
            >
              Open booth
            </Button>
          </div>
        </div>
      </Modal>
    </BuilderProvider>
  );
}

/**
 * Publish blocked by schema validation.
 *
 * Errors come back as a nested path→messages map from dry-schema, which is
 * unreadable raw — flattened here into "where: what".
 */
function ValidationModal({
  open, errors, onClose,
}: { open: boolean; errors?: unknown; onClose: () => void }) {
  const lines = flattenErrors(errors);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="This configuration can't be published yet"
      description="Fix the following, then publish again. Nothing has changed on the live booth."
      footer={<Button variant="primary" onClick={onClose}>Got it</Button>}
    >
      <ul className="flex flex-col gap-2">
        {lines.length === 0 ? (
          <li className="text-sm text-haze-300">The server rejected the configuration but didn't say why.</li>
        ) : (
          lines.map((line) => (
            <li key={line} className="rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-haze-200">
              {line}
            </li>
          ))
        )}
      </ul>
    </Modal>
  );
}

function flattenErrors(errors: unknown, path: string[] = []): string[] {
  if (!errors) return [];
  if (typeof errors === "string") return [`${path.join(" → ")}: ${errors}`];
  if (Array.isArray(errors)) {
    if (errors.every((entry) => typeof entry === "string")) {
      return [`${path.join(" → ") || "Config"}: ${errors.join(", ")}`];
    }
    return errors.flatMap((entry, index) => flattenErrors(entry, [...path, `#${index + 1}`]));
  }
  if (typeof errors === "object") {
    return Object.entries(errors as Record<string, unknown>).flatMap(([key, value]) =>
      flattenErrors(value, [...path, key]),
    );
  }
  return [];
}
